import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  TextField,
  DatePicker,
  Typography,
  Select,
  FormLabel,
  Autocomplete,
  TextareaAutosize,
  FormHelperText,
  Chip,
  Box,
} from "@mui/material";
import { nanoid } from "nanoid";
import { useTheme } from "@mui/material/styles";
import OutlinedInput from "@mui/material/OutlinedInput";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField, TimePicker } from "@mui/x-date-pickers-pro";
import { makeStyles } from "@mui/styles";

const TextBoxField = React.forwardRef(
  (
    {
      label,
      error,
      placeholder,
      required,
      shrink,
      helperText,
      readOnly,
      variant,
      borderColor,
      ...props
    },
    ref
  ) => {
    const uniqueId = nanoid();

    return (
      <FormControl fullWidth>
        <TextField
          ref={ref}
          id={uniqueId}
          type='text'
          InputLabelProps={{
            shrink: shrink,
            sx: {
              marginTop: "-6px", // Moves the label a little up
              fontSize: "14px", // Optional: Customize label size
              backgroundColor: "white", // Prevents label overlap
              padding: "0 4px",
              width: "fit-content", // Prevents background from stretching
            },
            // sx: { transform: "translate(12px, 10px) " },
          }}
          error={error} // Pass the error prop to the TextField component
          FormHelperTextProps={{ sx: { color: error ? "#FF4C51" : "inherit" } }} // Apply color based on error
          label={label}
          variant={variant}
          inputProps={{
            readOnly: readOnly || false,
            style: { borderColor: "black", height: "10px", fontSize: "12px" },
          }}
          {...props}
          helperText={helperText}
        />
      </FormControl>
    );
  }
);
TextBoxField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,

  helperText: PropTypes.string,
  borderColor: PropTypes.string,
};

const SelectField = React.forwardRef(
  (
    {
      label,
      error,
      required,
      helperText,
      options,
      shrink,
      variant,
      margin,
      ...props
    },
    ref
  ) => (
    <FormControl fullWidth>
      <TextField
        ref={ref}
        labelId='demo-simple-select-label'
        id='demo-simple-select'
        type='select'
        select
        {...props}
        key={props.name}
        label={label}
        variant={variant}
        borderColor='black'
        InputLabelProps={{
          shrink: shrink,
          sx: {
            marginTop: "-6px", // Moves the label a little up
            fontSize: "14px", // Optional: Customize label size
            backgroundColor: "white", // Prevents label overlap
            padding: "0 4px",
            width: "fit-content", // Prevents background from stretching
          },
        }}
        error={error} // Pass the error prop to the TextField component
        FormHelperTextProps={{ sx: { color: error ? "#FF4C51" : "inherit" } }} // Apply color based on error
        helperText={helperText}
        SelectProps={{
          sx: { height: "28px" }, // Set height for select dropdown
        }}
        inputProps={{
          sx: { height: "10px" }, // Set height for input
        }}>
        {options.map((option) => (
          <MenuItem key={option} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>
    </FormControl>
  )
);
SelectField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,

  helperText: PropTypes.string,
};

const CheckboxField = React.forwardRef(
  ({ label, error, required, helperText, variant, ...props }, ref) => (
    <FormControl fullWidth>
      <FormLabel>{label}</FormLabel>
      <Checkbox
        id='demo-simple-check'
        labelId='demo-simple-check-label'
        ref={ref}
        type={"checkbox"}
        Checkbox
        row
        labelPlacement='start'
        variant={variant}
        helperText={helperText}
        error={error} // Pass the error prop to the TextField component
        FormHelperTextProps={{ sx: { color: error ? "#FF4C51" : "inherit" } }} // Apply color based on error
        {...props}
      />
    </FormControl>
  )
);
CheckboxField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
};

const RadioGroupField = React.forwardRef(
  (
    {
      id,
      label,
      error,
      required,
      helperText,
      options,
      variant,
      value,
      ...props
    },
    ref
  ) => {
    return (
      <FormControl fullWidth>
        <FormLabel>{label}</FormLabel>
        <RadioGroup
          id={id}
          ref={ref}
          row
          value={value}
          type={"radio"}
          helperText={helperText}
          variant={variant}
          sx={{
            gap: "5px", // Reduce space between options
          }}
          error={error} // Pass the error prop to the TextField component
          FormHelperTextProps={{ sx: { color: error ? "#FF4C51" : "inherit" } }} // Apply color based on error
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              required={required}
              name='gender'
              control={
                <Radio
                  sx={{
                    padding: "2px", // Reduce radio button padding
                    "& .MuiSvgIcon-root": {
                      fontSize: "14px", // Smaller radio icon
                    },
                  }}
                />
              }
              checked={value === option.value}
              sx={{
                fontSize: "10px",
              }}
              {...props}
              label={
                <span style={{ color: error ? "#FF4C51" : "inherit" }}>
                  {option.label}
                </span>
              } // Apply color based on error
            />
          ))}
        </RadioGroup>
      </FormControl>
    );
  }
);
RadioGroupField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
};

const DateField = React.forwardRef(
  ({ label, error, required, helperText, variant, ...props }, ref) => (
    <FormControl fullWidth>
      {/* <DatePicker
        ref={ref}
        selected={props.value}
        onChange={(date) => props.onChange(date)} // Pass the onChange function as a prop
        dateFormat="MM/dd/yyyy" // Customize the date format as needed
        {...props}
      /> */}
      <TextField
        ref={ref}
        type='date'
        date
        label={label} // Include the label prop here
        InputLabelProps={{ shrink: true }} // Ensure the label shrinks when there is a value
        selected={props.value}
        error={error} // Pass the error prop to the TextField component
        FormHelperTextProps={{ sx: { color: error ? "#FF4C51" : "inherit" } }} // Apply color based on error
        helperText={helperText}
        variant={variant}
        sx={{
          height: "30px", // Set height here
          "& .MuiInputBase-root": {
            height: "32px", // Input field height
            fontSize: "12px", // Font size inside the input field
          },
          "& .MuiFormLabel-root": {
            fontSize: "12px", // Label font size
          },
        }}
        {...props}
      />
    </FormControl>
  )
);
DateField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
};
const AutoCompleteField = React.forwardRef(
  (
    {
      label,
      error,
      placeholder,
      required,
      helperText,
      options,
      variant,
      ...props
    },
    ref
  ) => {
    const uniqueId = nanoid();

    return (
      <FormControl fullWidth sx={{ height: "20px" }}>
        <Autocomplete
          sx={{ padding: "unset", height: "20px" }}
          ref={ref}
          id={uniqueId}
          freeSolo
          options={options}
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              padding: "0px !important", // Remove default padding
              height: "30px", // Set the height for Autocomplete input field
            },
            "& .MuiOutlinedInput-root": {
              height: "30px", // Apply height to the outlined variant
            },
            "& .MuiInputBase-input": {
              padding: "1px !important", // Optional padding inside the input
              height: "20px !important", // Actual input text height
            },
          }}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              type='text'
              error={error}
              FormHelperTextProps={{
                sx: { color: error ? "#FF4C51" : "inherit" },
              }}
              label={label}
              required={required}
              variant={variant}
              helperText={helperText}
            />
          )}
          {...props}
        />
      </FormControl>
    );
  }
);

AutoCompleteField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.object).isRequired, // Adjust the PropTypes for options based on your data structure
};
const TimePickerField = React.forwardRef(
  ({ label, error, required, helperText, value, onChange, ...props }, ref) => {
    const uniqueId = nanoid();

    return (
      <FormControl fullWidth>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["TimePicker"]}>
            <TimePicker
              label={label}
              value={value}
              onChange={onChange}
              format='hh:mm a'
              minutesStep={1}
              timeSteps={{ minutes: 1 }}
              sx={{ width: "383px" }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px", // Set the height for the input field
                  padding: "0px",
                },
                "& .MuiInputBase-input": {
                  padding: "10px ", // Text input padding
                  height: "20px", // Height of the input text
                },
                width: "380px", // Optional width
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  type='text'
                  error={error}
                  FormHelperTextProps={{
                    sx: { color: error ? "#FF4C51" : "inherit" },
                  }}
                  label={label}
                  required={required}
                  helperText={helperText}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "20px", // Set the height for the input field
                    },
                    "& .MuiInputBase-input": {
                      padding: "2px ", // Adjust text padding
                      height: "20px",
                    },
                  }}
                />
              )}
              {...props}
            />
          </DemoContainer>
        </LocalizationProvider>
      </FormControl>
    );
  }
);

TimePickerField.propTypes = {
  label: PropTypes.string.isRequired,
  error: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
  value: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
};
const MultilineComponent = ({
  label,
  error,
  required,
  helperText,
  value,
  onChange,
  placeholder,
  ...props
}) => {
  return (
    <FormControl fullWidth error={error} required={required}>
      {label && <InputLabel>{label}</InputLabel>}
      <TextareaAutosize
        value={value}
        aria-label='empty textarea'
        onChange={onChange}
        placeholder={placeholder}
        minRows={3} // You can adjust the number of rows as needed
        {...props}
      />
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
    </FormControl>
  );
};
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 300,
      width: 250,
    },
  },
};

const MultilineSelect = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  ...props
}) => {
  return (
    <FormControl fullWidth>
      {label && <InputLabel>{label}</InputLabel>}
      <Select
        multiple
        value={value}
        onChange={onChange}
        input={<InputLabel />}
        renderValue={(selected) => (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {selected.map((val) => (
              <Chip key={val} label={val} />
            ))}
          </div>
        )}
        MenuProps={MenuProps}
        {...props}>
        {options.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
MultilineSelect.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  required: PropTypes.bool,
  helperText: PropTypes.string,
};
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps1 = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};
const MultiSelectField = React.forwardRef(
  (
    { label, options, value = [], onChange, error, helperText, ...props },
    ref
  ) => {
    const theme = useTheme();

    return (
      <FormControl fullWidth error={error}>
        <InputLabel>{label}</InputLabel>
        <Select
          multiple
          value={value}
          onChange={onChange}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) => (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {selected.map((value) => {
                const option = options.find((opt) => opt.value === value);
                return (
                  <Chip key={value} label={option ? option.label : value} />
                );
              })}
            </Box>
          )}
          {...props}>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {helperText && (
          <p style={{ color: error ? "#FF4C51" : "#797F8F", fontSize: "12px" }}>
            {helperText}
          </p>
        )}
      </FormControl>
    );
  }
);

MultiSelectField.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.bool,
  helperText: PropTypes.string,
};

export {
  TextBoxField,
  SelectField,
  CheckboxField,
  RadioGroupField,
  DateField,
  AutoCompleteField,
  TimePickerField,
  MultilineComponent,
  MultilineSelect,
  MultiSelectField,
};
